import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Edit2, Trash2, CheckCircle, Phone, Mail,
  Calendar, Filter, X, AlertTriangle, ToggleLeft, ToggleRight,
  Repeat, CreditCard
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const emptyForm = {
  name: '', email: '', phone: '', notes: '',
  monthly: { enabled: false, startDate: '', price: '', percentage: '0' },
  yearly:  { enabled: false, startDate: '', price: '', percentage: '0' }
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');

  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [paymentConfirm, setPaymentConfirm] = useState(null); // { client, type }

  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchClients(); }, [statusFilter, sortBy]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const response = await fetch(
        `${API_URL}/api/clients?status=${statusFilter}&sort=${sortBy}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) setClients(data.clients);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleBasicChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormError('');
  };

  const handleSubChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
    setFormError('');
  };

  const toggleSection = (section) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], enabled: !prev[section].enabled }
    }));
    setFormError('');
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingClient(null);
    setFormError('');
  };

  const openAddModal = () => { resetForm(); setShowModal(true); };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      phone: client.phone,
      notes: client.notes || '',
      monthly: client.monthly ? {
        enabled: true,
        startDate: client.monthly.startDate ? new Date(client.monthly.startDate).toISOString().split('T')[0] : '',
        price: client.monthly.price.toString(),
        percentage: client.monthly.percentage.toString()
      } : { enabled: false, startDate: '', price: '', percentage: '0' },
      yearly: client.yearly ? {
        enabled: true,
        startDate: client.yearly.startDate ? new Date(client.yearly.startDate).toISOString().split('T')[0] : '',
        price: client.yearly.price.toString(),
        percentage: client.yearly.percentage.toString()
      } : { enabled: false, startDate: '', price: '', percentage: '0' }
    });
    setShowModal(true);
  };

  const buildPayload = () => {
    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      notes: formData.notes
    };
    payload.monthly = formData.monthly.enabled ? {
      startDate: formData.monthly.startDate,
      price: parseFloat(formData.monthly.price),
      percentage: parseFloat(formData.monthly.percentage) || 0
    } : null;
    payload.yearly = formData.yearly.enabled ? {
      startDate: formData.yearly.startDate,
      price: parseFloat(formData.yearly.price),
      percentage: parseFloat(formData.yearly.percentage) || 0
    } : null;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.monthly.enabled && !formData.yearly.enabled) {
      setFormError('Moras ukljuciti mesecnu ili godisnju naplatu (ili obe).');
      return;
    }
    if (formData.monthly.enabled && (!formData.monthly.startDate || !formData.monthly.price)) {
      setFormError('Mesecna naplata: datum i cena su obavezni.');
      return;
    }
    if (formData.yearly.enabled && (!formData.yearly.startDate || !formData.yearly.price)) {
      setFormError('Godisnja naplata: datum i cena su obavezni.');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('mstechs_token');
      const url = editingClient
        ? `${API_URL}/api/clients/${editingClient._id}`
        : `${API_URL}/api/clients`;
      const response = await fetch(url, {
        method: editingClient ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(buildPayload())
      });
      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        resetForm();
        fetchClients();
      } else {
        setFormError(data.message);
      }
    } catch (err) {
      setFormError('Greska pri cuvanju.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (clientId) => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) { setDeleteConfirm(null); fetchClients(); }
    } catch (err) { console.error(err); }
  };

  const handlePayment = async (client, type) => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const endpoint = type === 'monthly' ? 'pay-monthly' : 'pay-yearly';
      await fetch(`${API_URL}/api/clients/${client._id}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      setPaymentConfirm(null);
      fetchClients();
    } catch (err) { console.error(err); }
  };

  const handleToggleStatus = async (clientId) => {
    try {
      const token = localStorage.getItem('mstechs_token');
      await fetch(`${API_URL}/api/clients/${clientId}/toggle-status`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchClients();
    } catch (err) { console.error(err); }
  };

  const formatCurrency = (a) => new Intl.NumberFormat('sr-RS').format(a || 0) + ' RSD';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('sr-RS') : '-';
  const isOverdue = (d) => d && new Date(d) < new Date();
  const isDueSoon = (d) => {
    if (!d) return false;
    const days = (new Date(d) - new Date()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 7;
  };

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const calculateTotal = (price, percentage) => {
    const p = parseFloat(price) || 0;
    const pct = parseFloat(percentage) || 0;
    return p + (p * pct / 100);
  };

  if (loading) {
    return <div className="admin-loading"><div className="admin-spinner"></div></div>;
  }

  return (
    <div className="clients-page">
      <div className="page-header">
        <div className="header-actions">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Pretrazi klijente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <Filter size={18} />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Svi statusi</option>
              <option value="aktivan">Aktivni</option>
              <option value="neaktivan">Neaktivni</option>
            </select>
          </div>
          <div className="filter-group">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="createdAt">Najnoviji</option>
              <option value="name">Po imenu</option>
              <option value="nextMonthly">Po mesecnoj naplati</option>
              <option value="nextYearly">Po godisnjoj naplati</option>
            </select>
          </div>
          <button className="btn-add" onClick={openAddModal}>
            <Plus size={20} /> Dodaj klijenta
          </button>
        </div>
      </div>

      <div className="clients-grid">
        {filteredClients.length > 0 ? filteredClients.map((client) => (
          <div
            key={client._id}
            className={`client-card ${client.status === 'neaktivan' ? 'inactive' : ''}`}
          >
            <div className="client-card-header">
              <div className="client-card-title">
                <h3>{client.name}</h3>
                <button
                  className={`status-badge ${client.status}`}
                  onClick={() => handleToggleStatus(client._id)}
                >
                  {client.status === 'aktivan'
                    ? <><ToggleRight size={14} /> Aktivan</>
                    : <><ToggleLeft size={14} /> Neaktivan</>}
                </button>
              </div>
              <div className="client-card-actions">
                <button className="btn-icon edit" onClick={() => openEditModal(client)}>
                  <Edit2 size={16} />
                </button>
                <button className="btn-icon delete" onClick={() => setDeleteConfirm(client)}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="client-card-contact">
              <a href={`mailto:${client.email}`}><Mail size={14} /> {client.email}</a>
              <a href={`tel:${client.phone}`}><Phone size={14} /> {client.phone}</a>
            </div>

            <div className="payment-blocks">
              {client.monthly && (
                <div className="payment-block monthly">
                  <div className="payment-block-header">
                    <Repeat size={16} />
                    <span>Mesecna</span>
                  </div>
                  <div className="payment-block-amount">
                    {formatCurrency(client.monthly.total)}
                  </div>
                  {client.monthly.percentage > 0 && (
                    <div className="payment-block-meta">
                      {formatCurrency(client.monthly.price)} + {client.monthly.percentage}%
                    </div>
                  )}
                  <div className={`payment-block-date ${isOverdue(client.monthly.nextDate) ? 'overdue' : isDueSoon(client.monthly.nextDate) ? 'soon' : ''}`}>
                    <Calendar size={14} />
                    <span>Sledeca: {formatDate(client.monthly.nextDate)}</span>
                    {isOverdue(client.monthly.nextDate) && <AlertTriangle size={14} />}
                  </div>
                  {client.status === 'aktivan' && (
                    <button
                      className="payment-btn-card monthly"
                      onClick={() => setPaymentConfirm({ client, type: 'monthly' })}
                    >
                      <CreditCard size={14} /> Naplati mesecnu
                    </button>
                  )}
                </div>
              )}

              {client.yearly && (
                <div className="payment-block yearly">
                  <div className="payment-block-header">
                    <Calendar size={16} />
                    <span>Godisnja</span>
                  </div>
                  <div className="payment-block-amount">
                    {formatCurrency(client.yearly.total)}
                  </div>
                  {client.yearly.percentage > 0 && (
                    <div className="payment-block-meta">
                      {formatCurrency(client.yearly.price)} + {client.yearly.percentage}%
                    </div>
                  )}
                  <div className={`payment-block-date ${isOverdue(client.yearly.nextDate) ? 'overdue' : isDueSoon(client.yearly.nextDate) ? 'soon' : ''}`}>
                    <Calendar size={14} />
                    <span>Sledeca: {formatDate(client.yearly.nextDate)}</span>
                    {isOverdue(client.yearly.nextDate) && <AlertTriangle size={14} />}
                  </div>
                  {client.status === 'aktivan' && (
                    <button
                      className="payment-btn-card yearly"
                      onClick={() => setPaymentConfirm({ client, type: 'yearly' })}
                    >
                      <CreditCard size={14} /> Naplati godisnju
                    </button>
                  )}
                </div>
              )}
            </div>

            {client.notes && (
              <div className="client-card-notes">
                <span>{client.notes}</span>
              </div>
            )}
          </div>
        )) : (
          <div className="empty-clients">
            <p>Nema pronadjenih klijenata</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClient ? 'Izmeni klijenta' : 'Dodaj novog klijenta'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              {formError && (
                <div className="form-error">
                  <AlertTriangle size={18} /> {formError}
                </div>
              )}

              <h4 className="form-section-title">Osnovni podaci</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Naziv klijenta *</label>
                  <input
                    type="text" name="name"
                    value={formData.name} onChange={handleBasicChange} required
                  />
                </div>
              </div>
              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email" name="email"
                    value={formData.email} onChange={handleBasicChange} required
                  />
                </div>
                <div className="form-group">
                  <label>Telefon *</label>
                  <input
                    type="tel" name="phone"
                    value={formData.phone} onChange={handleBasicChange} required
                  />
                </div>
              </div>

              <div className="form-section-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.monthly.enabled}
                    onChange={() => toggleSection('monthly')}
                  />
                  <span>Mesecna naplata</span>
                </label>
              </div>
              {formData.monthly.enabled && (
                <div className="form-section-content">
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>Datum pocetka *</label>
                      <input
                        type="date"
                        value={formData.monthly.startDate}
                        onChange={(e) => handleSubChange('monthly', 'startDate', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Cena (RSD) *</label>
                      <input
                        type="number" min="0"
                        value={formData.monthly.price}
                        onChange={(e) => handleSubChange('monthly', 'price', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>Procenat (%)</label>
                      <input
                        type="number" min="0" max="100"
                        value={formData.monthly.percentage}
                        onChange={(e) => handleSubChange('monthly', 'percentage', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Ukupno mesecno</label>
                      <div className="calculated-total">
                        {formatCurrency(calculateTotal(formData.monthly.price, formData.monthly.percentage))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-section-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.yearly.enabled}
                    onChange={() => toggleSection('yearly')}
                  />
                  <span>Godisnja naplata</span>
                </label>
              </div>
              {formData.yearly.enabled && (
                <div className="form-section-content">
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>Datum pocetka *</label>
                      <input
                        type="date"
                        value={formData.yearly.startDate}
                        onChange={(e) => handleSubChange('yearly', 'startDate', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Cena (RSD) *</label>
                      <input
                        type="number" min="0"
                        value={formData.yearly.price}
                        onChange={(e) => handleSubChange('yearly', 'price', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>Procenat (%)</label>
                      <input
                        type="number" min="0" max="100"
                        value={formData.yearly.percentage}
                        onChange={(e) => handleSubChange('yearly', 'percentage', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Ukupno godisnje</label>
                      <div className="calculated-total">
                        {formatCurrency(calculateTotal(formData.yearly.price, formData.yearly.percentage))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Napomene</label>
                  <textarea
                    name="notes" rows="3"
                    value={formData.notes} onChange={handleBasicChange}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Otkazi
                </button>
                <button type="submit" className="btn-save" disabled={submitting}>
                  {submitting ? 'Cuvanje...' : 'Sacuvaj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Potvrda brisanja</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>Obrisati klijenta <strong>{deleteConfirm.name}</strong>?</p>
              <p className="warning-text">Ova akcija brise i sve uplate.</p>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Otkazi</button>
              <button className="btn-delete" onClick={() => handleDelete(deleteConfirm._id)}>Obrisi</button>
            </div>
          </div>
        </div>
      )}

      {paymentConfirm && (
        <div className="modal-overlay" onClick={() => setPaymentConfirm(null)}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Potvrda uplate</h2>
              <button className="modal-close" onClick={() => setPaymentConfirm(null)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Evidentirati <strong>{paymentConfirm.type === 'monthly' ? 'mesecnu' : 'godisnju'}</strong> uplatu za klijenta <strong>{paymentConfirm.client.name}</strong>?
              </p>
              <div className="payment-confirm-details">
                <p><strong>Iznos:</strong> {formatCurrency(
                  paymentConfirm.type === 'monthly'
                    ? paymentConfirm.client.monthly?.total
                    : paymentConfirm.client.yearly?.total
                )}</p>
                <p><strong>Sledeca naplata:</strong> {formatDate(
                  paymentConfirm.type === 'monthly'
                    ? paymentConfirm.client.monthly?.nextDate
                    : paymentConfirm.client.yearly?.nextDate
                )}</p>
              </div>
              <p className="info-text">
                Datum sledece naplate ce biti pomeren {paymentConfirm.type === 'monthly' ? 'mesec' : 'godinu'} unapred.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setPaymentConfirm(null)}>Otkazi</button>
              <button
                className="btn-save"
                onClick={() => handlePayment(paymentConfirm.client, paymentConfirm.type)}
              >
                <CheckCircle size={16} /> Evidentiraj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;